// src/contexts/AgentSpaceContext.js
// Shared state for the Agent Space FAB and the Agent Space page.
//   - selectedAgent: currently picked agent (drives FAB chat + info drawer
//     and the AgentSpace page's workflows table when the user is there)
//   - fabOpen: whether the floating chat + info drawer are visible
//   - fabVisible: whether the floating chat BUBBLE is shown at all (user
//     can hide it via the toggle on the Agent Space landing page).
//     Persisted to localStorage so the preference survives reloads.
//   - workflowsRefreshKey: bumped after every message/schedule so info
//     panels and the workflows table behind them refetch
//
// The Provider lives in ChatbotLayout so the FAB is available on EVERY
// chatbot panel page (not just /agents/agent-space).
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const AgentSpaceContext = createContext(null);

const FAB_VISIBLE_STORAGE_KEY = 'agentSpace.fabVisible';

export function AgentSpaceProvider({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { chatbotId } = useParams();

    const [selectedAgent, setSelectedAgentState] = useState(null);
    const [fabOpen, setFabOpen] = useState(false);
    // Visibility of the floating bubble itself, persisted across reloads.
    // Defaults to true. Hiding it also forces the open chat to close.
    const [fabVisible, setFabVisibleState] = useState(() => {
        try {
            const stored = window.localStorage.getItem(FAB_VISIBLE_STORAGE_KEY);
            return stored === null ? true : stored === 'true';
        } catch (e) {
            return true;
        }
    });
    useEffect(() => {
        try { window.localStorage.setItem(FAB_VISIBLE_STORAGE_KEY, String(fabVisible)); } catch (e) { /* ignore */ }
    }, [fabVisible]);
    const setFabVisible = useCallback((next) => {
        setFabVisibleState((prev) => {
            const resolved = typeof next === 'function' ? next(prev) : next;
            if (!resolved) setFabOpen(false);
            return resolved;
        });
    }, []);
    const toggleFabVisible = useCallback(() => setFabVisible((v) => !v), [setFabVisible]);
    const [workflowsRefreshKey, setWorkflowsRefreshKey] = useState(0);
    // What the Agent Space page shows for the selectedAgent:
    //   'workflows' â€” the workflows/activity table (default, via selectAgent)
    //   'settings'  â€” the configuration screen (via configureAgent / gear icon)
    const [agentMode, setAgentMode] = useState('workflows');

    // Pick an agent â€” auto-opens the FAB AND navigates the back canvas to
    // the Agent Space workflows page for that agent (so the user can see
    // its workflows table behind the FAB). Already-on-agent-space stays put.
    const selectAgent = useCallback((agentId) => {
        setSelectedAgentState(agentId);
        setAgentMode('workflows');
        setFabOpen(true);
        if (chatbotId) {
            const target = `/chatbot/${chatbotId}/agents/agent-space`;
            if (location.pathname !== target) {
                navigate(target);
            }
        }
    }, [chatbotId, navigate, location.pathname]);

    // Configure an agent â€” closes the FAB chat panel and lands the user
    // on the agent's configuration screen (connection, prompt, etc.).
    // Used by the gear icon in the agents list.
    const configureAgent = useCallback((agentId) => {
        setSelectedAgentState(agentId);
        setAgentMode('settings');
        setFabOpen(false);
        if (chatbotId) {
            const target = `/chatbot/${chatbotId}/agents/agent-space`;
            if (location.pathname !== target) {
                navigate(target);
            }
        }
    }, [chatbotId, navigate, location.pathname]);

    const clearSelectedAgent = useCallback(() => {
        setSelectedAgentState(null);
        setAgentMode('workflows');
    }, []);

    const bumpWorkflows = useCallback(() => {
        setWorkflowsRefreshKey((k) => k + 1);
    }, []);

    return (
        <AgentSpaceContext.Provider
            value={{
                selectedAgent,
                setSelectedAgent: setSelectedAgentState,
                selectAgent,
                configureAgent,
                clearSelectedAgent,
                agentMode,
                setAgentMode,
                fabOpen,
                setFabOpen,
                fabVisible,
                setFabVisible,
                toggleFabVisible,
                workflowsRefreshKey,
                bumpWorkflows,
            }}
        >
            {children}
        </AgentSpaceContext.Provider>
    );
}

export function useAgentSpace() {
    const ctx = useContext(AgentSpaceContext);
    if (!ctx) {
        throw new Error('useAgentSpace must be used inside <AgentSpaceProvider>');
    }
    return ctx;
}

// Safe-fall variant: returns null when used outside provider (used by
// components that can render in either tree).
export function useAgentSpaceMaybe() {
    return useContext(AgentSpaceContext);
}
